import React from "react";
import { useParams, Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import SEO from "../components/SEO";

const CONTACT = "o.creasubli@gmail.com";

export const LEGAL_DOCS = {
  "mentions-legales": {
    title: "Mentions légales",
    updated: "2026",
    sections: [
      { h: "Éditeur du site", p: `Le site o-crea est édité par o-crea Artisanat, société individuelle, 1513 Rossenges, Suisse. Contact : ${CONTACT}.` },
      { h: "Responsable de la publication", p: "o-crea Artisanat." },
      { h: "Hébergement", p: "Le site est hébergé par notre prestataire d'hébergement." },
      { h: "TVA", p: "o-crea Artisanat n'est pas assujettie à la TVA. Les prix affichés sont en francs suisses (CHF), sans TVA." },
      { h: "Propriété intellectuelle", p: "L'ensemble des contenus du site (textes, visuels, logo, créations) est protégé. Toute reproduction sans autorisation est interdite." },
      { h: "Responsabilité", p: "o-crea s'efforce d'assurer l'exactitude des informations diffusées mais ne saurait être tenu responsable d'erreurs ou d'indisponibilités du site." },
    ],
  },
  cgv: {
    title: "Conditions générales de vente",
    updated: "2026",
    sections: [
      { h: "1. Champ d'application", p: "Les présentes CGV régissent les ventes réalisées sur le site o-crea auprès de tout acheteur. Toute commande implique l'acceptation pleine et entière des présentes conditions." },
      { h: "2. Produits & personnalisation", p: "o-crea propose des objets personnalisables (gravure laser, sublimation) ainsi que des produits déjà réalisés. Les visuels sont indicatifs ; chaque pièce étant faite main, de légères variations peuvent exister." },
      { h: "3. Prix", p: "Les prix sont indiqués en francs suisses (CHF). o-crea Artisanat n'étant pas assujettie à la TVA, aucune TVA n'est facturée. Les frais de livraison sont calculés selon le poids de la commande et affichés avant le paiement." },
      { h: "4. Commande & paiement", p: "La commande est validée après paiement sécurisé via Stripe. o-crea se réserve le droit de refuser une commande en cas de litige ou de demande manifestement abusive." },
      { h: "5. Livraison", p: "Les commandes sont expédiées en Suisse. Délais indicatifs : 3 à 7 jours ouvrés pour une pièce personnalisée, 48h pour un produit déjà réalisé. Les frais de port suivent le barème au poids (dès 3.00 CHF)." },
      { h: "6. Droit de rétractation", p: "Les articles personnalisés (avec texte, image ou gravure à la demande) sont réalisés spécialement pour vous et ne peuvent être ni repris ni échangés, sauf défaut de fabrication." },
      { h: "7. Réclamations", p: `Pour toute question ou réclamation, contactez-nous à ${CONTACT}. En cas de produit défectueux, une photo pourra vous être demandée.` },
      { h: "8. Droit applicable", p: "Les présentes CGV sont soumises au droit suisse. Tout litige relève des tribunaux compétents du canton concerné." },
    ],
  },
  confidentialite: {
    title: "Politique de confidentialité",
    updated: "2026",
    sections: [
      { h: "Données collectées", p: "Nous collectons uniquement les données nécessaires au traitement de votre commande : adresse e-mail, adresse de livraison, contenu de la commande et informations de paiement (gérées directement par Stripe)." },
      { h: "Finalités", p: "Ces données servent à traiter et expédier votre commande, à vous envoyer les e-mails de confirmation et d'expédition, et à répondre à vos demandes." },
      { h: "Destinataires", p: "Vos données ne sont jamais revendues. Elles sont partagées uniquement avec nos prestataires techniques : Stripe (paiement) et notre service d'envoi d'e-mails, dans la stricte mesure nécessaire." },
      { h: "Conservation", p: "Les données de commande sont conservées le temps requis par nos obligations légales et comptables." },
      { h: "Vos droits", p: `Vous disposez d'un droit d'accès, de rectification et de suppression de vos données. Pour l'exercer, écrivez-nous à ${CONTACT}.` },
    ],
  },
  cookies: {
    title: "Politique de cookies",
    updated: "2026",
    sections: [
      { h: "À quoi servent les cookies", p: "o-crea utilise uniquement des cookies et un stockage local strictement nécessaires au fonctionnement du site : maintien de votre session administrateur et mémorisation de votre panier." },
      { h: "Pas de traçage publicitaire", p: "Nous n'utilisons pas de cookies publicitaires ni de traçage à des fins marketing." },
      { h: "Gestion", p: "Vous pouvez à tout moment supprimer les cookies et le stockage local via les réglages de votre navigateur. La suppression du panier videra vos articles en cours." },
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const doc = LEGAL_DOCS[slug];

  if (!doc) {
    return (
      <div className="mx-auto flex min-h-[50vh] max-w-[900px] flex-col items-start justify-center px-5 md:px-10">
        <h1 className="font-serif text-4xl font-light text-ink">Page introuvable.</h1>
        <Link to="/" className="link-underline mt-6 text-sm uppercase tracking-widest text-ink">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[900px] px-5 pt-24 pb-28 md:px-10">
      <SEO title={doc.title} path={`/legal/${slug}`}
        description={`${doc.title} du site o-crea : informations légales, conditions de vente et de livraison, protection de vos données.`} />
      <Reveal>
        <p className="mb-3 text-xs uppercase tracking-[0.3em] text-terra">Informations légales</p>
        <h1 className="font-serif text-4xl font-light tracking-tight text-ink md:text-6xl">{doc.title}</h1>
        <p className="mt-3 text-sm text-ink3">Dernière mise à jour : {doc.updated}</p>
      </Reveal>
      <div className="mt-12 space-y-8">
        {doc.sections.map((s, i) => (
          <Reveal key={i} delay={i * 0.04} className="border-t border-line pt-6">
            <h2 className="font-serif text-xl font-medium text-ink md:text-2xl">{s.h}</h2>
            <p className="mt-2 max-w-2xl leading-relaxed text-ink2">{s.p}</p>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
